import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Collections } from '@local/client-contracts';
import { isLiveCollection, isWikiCollection } from '@local/common-web';
import { capitalCase } from '@local/ts-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuService } from '@shared/components/context-menu/context-menu.service';
import { DateFormat } from '@shared/consts';
import { EventsService } from '@shared/services';
import { KeyboardService } from '@shared/services/keyboard.service';
import { SessionService } from '@shared/services/session.service';
import { BreakpointsWidth, getWidthBreakpointScreen, windowSizeObserver } from '@shared/utils';
import { getTimeFromNowInText } from '@shared/utils/date-format.util';
import { isEqual } from 'lodash';
import moment from 'moment';
import { OverlayPanel } from 'primeng/overlaypanel';
import { filter } from 'rxjs';
import { AvatarItemModel } from 'src/app/bar/models/avatar-item.model';
import { CollectionResultData } from 'src/app/bar/models/collection-result-data';
import { WorkspacesService } from 'src/app/bar/services';
import { AvatarListService } from 'src/app/bar/services/avatar-list.service';
import { CollectionsUtilService } from 'src/app/bar/services/collections-util.service';
import { CollectionsService } from 'src/app/bar/services/collections.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { ResultsService } from 'src/app/bar/services/results.service';
import { CollectionContextMenuHelper } from '../../helpers/collection-context-menu-helper';
import { collectionContent } from '../../helpers/collection.content';
import { CollectionOptionsId } from '../../models';
import { ShareOptionsService } from 'src/app/bar/services/share-options.service';
import { Constants } from '@local/common';

@UntilDestroy()
@Component({
  selector: 'collection-header-line',
  templateUrl: './collection-header-line.component.html',
  styleUrls: ['./collection-header-line.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionHeaderLineComponent implements AfterViewInit, OnInit {
  private readonly largeScreenBreakpoints: BreakpointsWidth[] = ['large', 'extra-large'];
  minimizeHeaderText: boolean;

  @Input() collectionResultData: CollectionResultData;

  private _collection: Collections.Collection;
  @Input() set collection(val: Collections.Collection) {
    if (!val) return;

    const prevCollection = this.collection;
    this._collection = val;

    this.isFavorite = !!this.collection?.favoriteMarkedTime;

    this.isOwner = this.workspaceService.isMe(this.collection.accountId);

    if (this.collection?.modifiedTime && !isEqual(prevCollection?.modifiedTime, this.collection?.modifiedTime)) {
      this.getHeaderText();
    }

    if (
      (!prevCollection && this.collection) ||
      !this.shareOptionsService.compareShareOptions(prevCollection?.shareOptions, this.collection?.shareOptions)
    ) {
      this.getAvatarList();
    }

    if (this.collection && !isLiveCollection(this.collection)) {
      this.lastSyncText = null;
    }

    this.cdr.markForCheck();
  }

  get collection(): Collections.Collection {
    return this._collection;
  }

  get favoriteTooltipText(): string {
    return `${this.isFavorite ? 'Remove from' : 'Add to'} Favorites`;
  }

  set lastSync(val: number) {
    if (val) {
      this.lastSyncText = getTimeFromNowInText(val);
      this.lastSyncActual = moment(val).format(DateFormat.FULL_DATE_HOURS_A);
    } else {
      this.lastSyncText = this.lastSyncActual = null;
    }
  }

  @ViewChild('actions', { read: ElementRef }) actionsRef: ElementRef;

  //collection fields formatted
  lastSyncText: string;
  lastSyncActual: string;
  displayModifiedTime: string;
  preDisplayModifiedTime: string;
  modifiedTimeActual: string;
  startModifiedPhrase: string;
  contextMenuActive: boolean;
  isFavorite: boolean;
  DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG = Constants.DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG;

  //share
  avatarsList: AvatarItemModel[] = [];

  isOwner: boolean;
  username: string;

  private collectionContextMenuHelper: CollectionContextMenuHelper;

  get canEdit(): boolean {
    return this.collectionsHelperService.canEdit(this.collection);
  }

  get canEditPin(): boolean {
    return this.collectionsHelperService.canEdit(this.collection, 'pin');
  }

  get lastUpdateMessage() {
    return `${this.startModifiedPhrase} ${this.preDisplayModifiedTime} this ${this.displayModifiedTime}`;
  }

  get lastUpdateShortMessage() {
    return `Updated ${this.displayModifiedTime}`;
  }

  get lastSyncMessage() {
    return this.lastSyncText ? `Last synced ${this.lastSyncText}` : 'Last sync time is currently unavailable';
  }

  get lastSyncAndUpdateTime() {
    return this.lastSyncText ? `${this.lastUpdateMessage} | Last synced ${this.lastSyncText}` : `${this.lastUpdateMessage}`;
  }

  constructor(
    public collectionsService: CollectionsService,
    private contextMenuService: ContextMenuService,
    private cdr: ChangeDetectorRef,
    private keyboardService: KeyboardService,
    private eventsService: EventsService,
    public resultsService: ResultsService,
    private hubService: HubService,
    private collectionsHelperService: CollectionsUtilService,
    private workspaceService: WorkspacesService,
    private sessionService: SessionService,
    private avatarListService: AvatarListService,
    private shareOptionsService: ShareOptionsService
  ) {}

  ngOnInit(): void {
    this.collectionResultData.collectionResultData$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x)
      )
      .subscribe((resultData) => {
        if (this.collection) {
          this.lastSync = resultData.lastSyncTime;
          this.cdr.markForCheck();
        }
      });
    this.initUserName();

    windowSizeObserver(100)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.minimizeHeaderText = !this.largeScreenBreakpoints.includes(getWidthBreakpointScreen());
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    this.initContextMenuHelper();
    this.initContextMenuHelperListeners();
  }

  private initUserName() {
    this.sessionService.current$.pipe(untilDestroyed(this)).subscribe((session) => {
      this.username = session?.user?.firstName;
      this.cdr.markForCheck();
    });
  }

  onPinClick() {
    if (!this.canEditPin) return;
    let target = '';
    const label = 'mouse_click';
    if (this.collection?.pinBy) {
      target = this.collectionsService.isOwnerOrAdmin && this.collection?.isShared ? 'unpin_to_all' : 'unpin';
    } else {
      target = this.collectionsService.isOwnerOrAdmin && this.collection?.isShared ? 'pin_to_all' : 'pin';
    }

    this.eventsService.event('collections.collection_actions', {
      target,
      label,
      location: { title: this.hubService.currentLocation },
    });
    this.collectionsService.openPinCollectionPopup(this.collection, label);
  }

  onFavoriteClick() {
    this.eventsService.event('collections.collection_actions', {
      target: `${!this.isFavorite ? 'add' : 'remove'}_favorite`,
      label: 'mouse_click',
      location: { title: this.hubService.currentLocation },
    });
    this.isFavorite = !this.isFavorite;
    this.collectionsService.toggleFavorite(this.collection);
  }

  private async getHeaderText() {
    this.displayModifiedTime = getTimeFromNowInText(this.collection.modifiedTime).toLowerCase();
    this.preDisplayModifiedTime =
      this.collection.modifiedTime === this.collection.createdTime ? collectionContent.created + ' ' : collectionContent.updated + ' ';
    this.modifiedTimeActual = moment(this.collection.modifiedTime).format(DateFormat.FULL_DATE_HOURS_A);

    const accountId = this.collection.modifiedBy;

    if (this.workspaceService.isMe(accountId)) {
      this.startModifiedPhrase = 'You';
    } else {
      const avatar = await this.avatarListService.getOwnerAvatar(accountId);
      this.startModifiedPhrase = capitalCase(avatar.name.replace('(Owner)', ''));
      if (avatar.isNameFromEmail) {
        this.modifiedTimeActual = `${avatar.email} ${this.modifiedTimeActual}`;
      }
    }

    this.cdr.markForCheck();
  }

  async openContextMenu(event: MouseEvent) {
    this.collectionsService.onFocusChange = 'open-context-menu';
    const removeItems: CollectionOptionsId[] = isWikiCollection(this.collection) ? ['make_a_copy'] : [];
    if (!this.collectionsService.isOwnerOrAdmin) {
      removeItems.push('pin');
    }
    const ref = await this.collectionContextMenuHelper.open(
      event,
      { pinBy: this.collection?.pinBy },
      this.canEdit ? 'collection' : 'collection-view',
      this.isFavorite,
      removeItems
    );
    this.contextMenuActive = true;
    ref.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextMenuActive = false;
      this.cdr.markForCheck();
    });
    this.eventsService.event('collections.open_menu', {
      label: 'mouse_click',
      location: { title: this.hubService.currentLocation },
    });
  }

  private initContextMenuHelper(): void {
    this.collectionContextMenuHelper = new CollectionContextMenuHelper(
      this.contextMenuService,
      this.keyboardService,
      this.actionsRef,
      this.hubService,
      this.workspaceService
    );
  }

  private initContextMenuHelperListeners() {
    this.collectionContextMenuHelper.invoke$
      .pipe(untilDestroyed(this))
      .subscribe(({ item, trigger }) => this.collectionsService.onContextMenuCommand(item, trigger, this.collection));
  }

  async getAvatarList() {
    this.avatarsList = await this.avatarListService.createAvatarList(this.collection.accountId, this.collection.shareOptions);
    this.cdr.markForCheck();
  }

  onShareClick() {
    this.collectionsService.shareCollection(this.collection);
  }

  onVerificationClick() {
    if (isWikiCollection(this.collection)) {
      this.collectionsService.openVerificationDetails(this.collection, this.collection.policy);
    }
  }

  onHidePanel(op: OverlayPanel) {
    op.overlayVisible = false;
    op.cd.markForCheck();
  }
}
